/**
 * Deterministic extraction of identity data and financial metrics from document text.
 */
import { ContactKind, ExtractedMetrics } from "./types";
import { compactTokens, findMoney, fold, MoneyMatch, parseCzechNumber, toMonths, toWeeks } from "./text";

/** Field labels that commonly appear in documents ("Label: value"), folded. */
const KNOWN_LABELS =
    "nazev (?:spolecnosti|firmy|subjektu)|obchodni firma|spolecnost|firma|subjekt|klient|zadavatel|kontaktni osoba|jmeno(?: a prijmeni)?|e-?mail|telefon|tel\\.?|mobil|typ subjektu|kategorie|ic|ico|dic|sidlo|adresa|web";

function labeledValue(original: string, folded: string, labels: string): string | null {
    const re = new RegExp(
        `(?:^|[\\s•|])(?:${labels})\\s*:\\s*(.+?)(?=\\s{2,}|\\s*[•\\n|;]|\\s+(?:${KNOWN_LABELS})\\s*:|$)`
    );
    const m = re.exec(folded);
    if (!m || !m[1]) return null;
    const start = m.index + m[0].length - m[1].length;
    const value = original.slice(start, start + m[1].length).trim();
    return value || null;
}

const LEGAL_FORM_RE =
    /([A-ZÁČĎÉĚÍŇÓŘŠŤÚŮÝŽ0-9][^\s,;:•]*(?:\s+[A-ZÁČĎÉĚÍŇÓŘŠŤÚŮÝŽ0-9&][^\s,;:•]*){0,4}),?\s+(s\.\s?r\.\s?o\.|spol\.\s?s\s?r\.\s?o\.|a\.\s?s\.|v\.\s?o\.\s?s\.|k\.\s?s\.|z\.\s?s\.)/;

const EMAIL_RE = /[a-z0-9._%+-]+@[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,}/;

export interface Identity {
    fullname: string;
    email: string;
    phone: string;
    ico: string;
    kind: ContactKind;
}

export function formatPhone(raw: string): string {
    let digits = raw.replace(/\D/g, "");
    if (digits.startsWith("00420")) digits = digits.slice(2);
    if (digits.length === 12 && digits.startsWith("420")) digits = digits.slice(3);
    if (digits.length !== 9) return raw.trim();
    return `+420 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
}

function detectKind(value: string | null, hasLegalForm: boolean, hasPerson: boolean): ContactKind {
    if (value) {
        const v = fold(value);
        if (/spolecnost|pravnick|firma/.test(v)) return "Společnost";
        if (/osoba|fyzick|osvc|podnikatel/.test(v)) return "Osoba";
        if (/lead|zajem/.test(v)) return "Lead";
    }
    if (hasLegalForm) return "Společnost";
    if (hasPerson) return "Osoba";
    return "Lead";
}

export function fileBaseName(fileName: string): string {
    return fileName.replace(/\.[a-z0-9]+$/i, "").replace(/[_-]+/g, " ").trim();
}

export function extractIdentity(original: string, fileName: string): Identity {
    const folded = fold(original);

    const company = labeledValue(original, folded, "nazev (?:spolecnosti|firmy|subjektu)|obchodni firma|spolecnost|firma|subjekt|klient|zadavatel");
    const legal = LEGAL_FORM_RE.exec(original);
    const legalName = legal ? `${legal[1]} ${legal[2]}`.replace(/\s+/g, " ").trim() : null;
    const person = labeledValue(original, folded, "kontaktni osoba|jmeno(?: a prijmeni)?");

    const labeledEmail = labeledValue(original, folded, "e-?mail");
    const emailSource = compactTokens(labeledEmail ?? original).toLowerCase();
    const email = EMAIL_RE.exec(emailSource)?.[0] ?? EMAIL_RE.exec(compactTokens(original).toLowerCase())?.[0] ?? "";

    const labeledPhone = labeledValue(original, folded, "telefon|tel\\.?|mobil");
    const phoneMatch =
        (labeledPhone && /\+?[\d\s-]{9,17}/.exec(labeledPhone)?.[0]) ||
        /(?:\+|00)420[\s-]?\d{3}[\s-]?\d{3}[\s-]?\d{3}/.exec(original)?.[0] ||
        /(?<![\d.,])[67]\d{2}[\s-]?\d{3}[\s-]?\d{3}(?![\d.,]|\s*(?:kc|czk|eur|mil|tis))/.exec(folded)?.[0] ||
        "";

    const ico = /\bic(?:o)?\s*:?\s*(\d{8})\b/.exec(folded)?.[1] ?? "";
    const kindLabel = labeledValue(original, folded, "typ subjektu|kategorie");

    return {
        fullname: (company || legalName || person || fileBaseName(fileName)).replace(/\s+/g, " ").trim(),
        email,
        phone: phoneMatch ? formatPhone(phoneMatch) : "",
        ico,
        kind: detectKind(kindLabel, !!(legalName || company), !!person),
    };
}

/* --- Financial metrics ---------------------------------------------------------------- */

type AmountClass = "revenue" | "profit" | "budget" | "equity" | "assets";

const AMOUNT_CLASSES: Array<[AmountClass, RegExp]> = [
    ["revenue", /obrat|trzb|vynos/g],
    ["profit", /zisk(u|em)?\b|vysledek hospodareni|ztrat/g],
    ["budget", /rozpocet|rozpoctu|investic|capex|cena (implementace|projektu|reseni)/g],
    ["equity", /vlastni kapital/g],
    ["assets", /aktiva celkem|celkova aktiva|bilancni sum/g],
];

/** Assigns an amount to the class whose keyword is closest before it (within the context window). */
function classifyAmount(match: MoneyMatch): AmountClass | null {
    const context = match.context.slice(-60);
    let best: AmountClass | null = null;
    let bestPos = -1;
    for (const [cls, re] of AMOUNT_CLASSES) {
        for (const m of Array.from(context.matchAll(re))) {
            const pos = m.index ?? -1;
            if (pos > bestPos) {
                bestPos = pos;
                best = cls;
            }
        }
    }
    return best;
}

const NUMBER_WORDS: Record<string, number> = {
    dve: 2, dvou: 2, dvema: 2, tri: 3, trech: 3, ctyri: 4, ctyr: 4, ctyrech: 4, pet: 5, peti: 5,
};

const firstNumber = (raw: string | undefined): number | null => (raw ? parseCzechNumber(raw) : null);

export function extractMetrics(foldedAll: string, foldedImplementation: string): ExtractedMetrics {
    const money = findMoney(foldedAll);
    const byClass: Partial<Record<AmountClass, number>> = {};
    for (const m of money) {
        const cls = classifyAmount(m);
        if (!cls || byClass[cls] !== undefined) continue;
        const isLoss = cls === "profit" && /ztrat/.test(m.context.slice(-40));
        byClass[cls] = isLoss ? -m.value : m.value;
    }

    const roi = /(?:\broi\b|navratnost\w*)[^.%]{0,50}?(\d+(?:[.,]\d+)?)\s*(mesic\w*|let|roky?|roku|tydn\w*)/.exec(foldedAll);
    const roiMonths = roi ? Math.round(toMonths(firstNumber(roi[1]) ?? 0, roi[2] ?? "")) : null;

    const growth =
        /(?:rust|narust|zvyseni)\s+(?:trzeb|trzby|obratu|zisku)[^.]{0,40}?(\d+(?:[.,]\d+)?)\s?%/.exec(foldedAll) ||
        /(?:trzby|obrat)\s+(?:mezirocne\s+)?vzrost\w*[^.]{0,30}?(\d+(?:[.,]\d+)?)\s?%/.exec(foldedAll);
    const decline =
        /(?:pokles|snizeni)\s+(?:trzeb|trzby|obratu|zisku)[^.]{0,40}?(\d+(?:[.,]\d+)?)\s?%/.exec(foldedAll) ||
        /(?:trzby|obrat)\s+(?:mezirocne\s+)?klesl\w*[^.]{0,30}?(\d+(?:[.,]\d+)?)\s?%/.exec(foldedAll);
    const growthPercent = growth ? firstNumber(growth[1]) : decline ? -(firstNumber(decline[1]) ?? 0) : null;

    const probabilities = Array.from(foldedAll.matchAll(/pravdepodobnost\w*[^.\d]{0,20}(\d+(?:[.,]\d+)?)\s?%/g))
        .map((m) => firstNumber(m[1]) ?? 0);
    const riskProbabilityPercent = probabilities.length ? Math.max(...probabilities) : null;

    const durations = Array.from(foldedImplementation.matchAll(/(\d+(?:[.,]\d+)?)\s*(tydn\w*|tyden|mesic\w*|mesice)/g))
        .map((m) => toWeeks(firstNumber(m[1]) ?? 0, m[2] ?? ""));
    const implementationWeeks = durations.length ? Math.round(durations.reduce((a, b) => a + b, 0)) : null;

    const phaseMatch = /(\d+|dve|dvou|dvema|tri|trech|ctyri|ctyr|ctyrech|pet|peti)\s+(?:faz|etap)/.exec(foldedImplementation);
    const phases = phaseMatch
        ? NUMBER_WORDS[phaseMatch[1] ?? ""] ?? firstNumber(phaseMatch[1])
        : null;

    return {
        revenue: byClass.revenue ?? null,
        profit: byClass.profit ?? null,
        budget: byClass.budget ?? null,
        equity: byClass.equity ?? null,
        assets: byClass.assets ?? null,
        roiMonths,
        growthPercent,
        riskProbabilityPercent,
        implementationWeeks,
        phases,
        amountsFound: money.length,
    };
}
